import type { ImageGravity } from "common/components/image-types";
import type { ImageSource } from "common/components/portable-renderers";
import { canMakeUrl, imageSourceUrl, MapTheme } from "common/components/portable-renderers";
import type { AppEnvironment } from "common/components/types";
import { defined } from "common/Support";
import type { OnClickHandler } from "hooks/use-on-click-from-action";
import * as React from "react";
import { withTheme } from "styled-components";
import type { Subtract } from "utility-types";
import { scaleImageWidthToHeight } from "../../../lib/image-utils";
import type { RuntimeTheme } from "../../../lib/theme/core-themes/runtime-theme";
import type { TileItemViewProps } from "../../views/tile-item/tile-item-view";
import TileItemView from "../../views/tile-item/tile-item-view";

interface ExtraProps {
    readonly appEnvironment: AppEnvironment;
    readonly theme: RuntimeTheme;
    readonly imageSource?: ImageSource;
    readonly gravity: ImageGravity;
}

interface Handled {
    readonly url?: string;
}

type Props = Subtract<TileItemViewProps, Handled> & ExtraProps;

const TileItem: React.FunctionComponent<Props> = (props: Props) => {
    const { onClick, theme, ...rest } = props;
    const [url, setURL] = React.useState<string>("");

    const { appEnvironment, imageSource, columnWidth: width, size, gravity } = rest;
    React.useEffect(() => {
        const f = async () => {
            const height = scaleImageWidthToHeight(width, size);
            const mapTheme = theme.mapTheme === "light" ? MapTheme.Light : MapTheme.Dark;
            if (canMakeUrl(imageSource)) {
                const newUrl = await imageSourceUrl(
                    appEnvironment,
                    defined(imageSource),
                    width,
                    height,
                    15,
                    mapTheme,
                    gravity
                );
                if (newUrl !== url) {
                    setURL(newUrl ?? "");
                }
            } else if (url !== "") {
                setURL("");
            }
        };
        void f();
    }, [appEnvironment, imageSource, width, size, theme, gravity, url]);

    const onClickCallback: OnClickHandler = React.useCallback(
        async (handled: boolean | undefined) => {
            if (onClick !== undefined) {
                return await onClick(handled);
            }
            return undefined;
        },
        [onClick]
    );

    return <TileItemView {...rest} url={url} onClick={onClickCallback} />;
};

export default withTheme(TileItem);
